import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import LegalConsent from "./LegalConsent";

/* ========== ANIMACIÓN ========== */
const slideDown = keyframes`
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

/* ========== ESTILOS BASE ========== */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background-color: #fff;
  border-radius: 15px;
  width: 100%;
  max-width: ${(p) => (p.$width ? p.$width : "500px")};
  padding: 30px 15px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1001;
  animation: ${slideDown} 0.3s ease;
  ${(p) =>
        p.$variant === "active" &&
        css`
      border: 1px solid #ff010270;
    `}
  ${(p) =>
        p.$variant === "chat" &&
        css`
      border-radius: 15px;
      padding: 15px;
    `}

  @media (max-width: 768px) {
    max-width: min(${(p) => (p.$width ? p.$width : "500px")}, 90vw);
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const Header = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const Label = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  color: #333;
  font-weight: bold;
`;

const Body = styled.div`
  overflow-y: hidden;
  height: ${(p) => p.$height || "auto"};
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const Button = styled.button`
  padding: 12px 30px;
  border: none;
  cursor: pointer;
  border-radius: 100px;
  outline: 0;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  transition: 0.3s ease;
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};

  ${(p) =>
        p.$variant === "accept" &&
        css`
      background-color: #fe0103;
      color: #fff;
      border: 1px solid #fe0103;

      &.line {
        background-color: transparent !important;
        color: #fe0103 !important;
        border: none !important;
        font-size: 18px;
      }

      &:hover {
        opacity: 0.9;
      }
    `}

  ${(p) =>
        p.$variant === "cancel" &&
        css`
      background-color: white;
      color: #fe0103;
      border: 1px solid #fe0103 !important;

      &:hover {
        opacity: 0.9;
      }
    `}
`;

const LegalSummary = styled.p`
  margin: 0;
  color: #374151;
  font-size: 14px;
  line-height: 1.6;
`;

const LegalFootnote = styled.p`
  margin: 12px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.6;
`;

const CenteredMessage = styled.p`
  text-align: center;
  margin: 0;
`;

const ActiveContent = styled.div`
  text-align: center;
`;

const ActiveTitle = styled.h2`
  margin-top: 10px;
`;

const SendContent = styled.div`
  text-align: center;
  padding: 20px;
`;

const SendFields = styled.div`
  margin-top: 20px;
`;

const SendInput = styled.input`
  width: 100%;
  padding: 8px;
  margin-bottom: 5px;
  border-radius: 5px;
  border: 1px solid #707070;

  & + & {
    margin-top: 10px;
  }
`;

const SendError = styled.p`
  color: red;
  font-size: 12px;
`;

const SendButton = styled.button`
  margin-top: 15px;
  padding: 10px;
  background-color: #fe0103;
  color: white;
  border: none;
  cursor: pointer;
  width: 100%;
  border-radius: 5px;
  outline: 0;
`;

/* ========== COMPONENTE PRINCIPAL ========== */
const Modal = ({
    isOpen,
    onClose,
    label = "",
    showLabel = true,
    children,
    acceptButtonText = "Aceptar",
    cancelButtonText = "Cancelar",
    onAccept,
    onCancel,
    width = "500px",
    acceptButton = true,
    cancelButton = true,
    buttonLine = false,
    height = "auto",
    modalContainerClass = "",
    acceptButtonDisabled = false,
    cancelButtonDisabled = false,
}) => {
    if (!isOpen) return null;

    const variant =
        modalContainerClass === "border-modal-active"
            ? "active"
            : modalContainerClass === "border-modal-chat-active"
                ? "chat"
                : "";

    return (
        <Overlay onClick={onClose}>
            <ModalContainer
                $width={width}
                $variant={variant}
                onClick={(e) => e.stopPropagation()}
            >
                {showLabel && label && (
                    <Header>
                        <Label>{label}</Label>
                    </Header>
                )}
                <Body $height={height}>{children}</Body>
                <BottomBar>
                    {cancelButton && (
                        <Button onClick={onCancel || onClose} $variant="cancel" disabled={cancelButtonDisabled}>
                            {cancelButtonText}
                        </Button>
                    )}
                    {acceptButton && (
                        <Button
                            onClick={onAccept}
                            $variant="accept"
                            className={buttonLine ? "line" : ""}
                            disabled={acceptButtonDisabled}
                        >
                            {acceptButtonText}
                        </Button>
                    )}
                </BottomBar>
            </ModalContainer>
        </Overlay>
    );
};

export default Modal;

/* ========== SUBCOMPONENTES ========== */
export const ModalConfirm = ({
    isOpen,
    onAccept,
    acceptButtonText = "Aceptar",
    onClose,
    cancelButtonText = "Cancelar",
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        showLabel={false}
        acceptButtonText={acceptButtonText}
        cancelButtonText={cancelButtonText}
        onAccept={onAccept}
        onCancel={onClose}
    >
        <CenteredMessage>
            ¿Estás seguro de que deseas continuar con esta acción?
        </CenteredMessage>
    </Modal>
);

export const TermsModal = ({ isOpen, onClose, onAccept }) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setIsChecked(false);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleAccept = async () => {
        if (!isChecked || isSubmitting) {
            alert("Debes aceptar los términos y la política de privacidad para continuar.");
            return;
        }

        try {
            setIsSubmitting(true);
            const result = await onAccept?.();
            if (result !== false) {
                onClose();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            label="Términos y privacidad"
            showLabel={false}
            onAccept={handleAccept}
            onCancel={onClose}
            acceptButtonText={isSubmitting ? "Guardando..." : "Aceptar"}
            cancelButtonText="Cancelar"
            width="90%"
        >
            <LegalSummary>
                Para continuar, revisa y acepta los documentos legales aplicables a tu cuenta.
            </LegalSummary>
            <LegalConsent
                audience="host"
                checked={isChecked}
                onChange={setIsChecked}
                disabled={isSubmitting}
            />
            <LegalFootnote>
                Al aceptar, también confirmas que la información proporcionada es verídica. El
                uso de datos falsos puede resultar en la suspensión o eliminación de tu cuenta.
            </LegalFootnote>
        </Modal>
    );
};

export const ActiveModal = ({ isOpen, onClose }) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        label="Términos de uso"
        showLabel={false}
        onAccept={onClose}
        onCancel={onClose}
        acceptButtonText="OK! Entendido"
        cancelButton={false}
        buttonLine={true}
        modalContainerClass="border-modal-active"
        width="90%"
    >
        <ActiveContent>
            <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
                    stroke="#17C405"
                    strokeWidth="1.5"
                />
                <path
                    d="M7.75 12L10.58 14.83L16.25 9.17"
                    stroke="#17C405"
                    strokeWidth="1.5"
                />
            </svg>
            <ActiveTitle>¡Felicitaciones!</ActiveTitle>
            <p>
                Ahora ya tienes activado tu código QR. Invita a todos
                tus clientes a escanearlo y dar un like.
            </p>
        </ActiveContent>
    </Modal>
);


export const SendModal = ({ isOpen, onClose, onSend }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState({ name: "", email: "" });

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSubmit = () => {
        let newErrors = { name: "", email: "" };

        if (!name.trim()) {
            newErrors.name = "El nombre es obligatorio.";
        }

        if (!email.trim()) {
            newErrors.email = "El correo es obligatorio.";
        } else if (!validateEmail(email)) {
            newErrors.email = "El correo no es válido.";
        }

        setErrors(newErrors);

        if (!newErrors.name && !newErrors.email) {
            onSend(name, email);
            setName("");
            setEmail("");
            setErrors({ name: "", email: "" });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            onAccept={onClose}
            label="Registro"
            showLabel={false}
            width="90%"
            cancelButton={false}
            acceptButtonText='cancelar'
            modalContainerClass="border-modal-active"
            buttonLine={true}
        >
            <SendContent>
                <h5>Ingresa tus datos</h5>
                <p>Por favor, ingresa tu nombre y correo para continuar.</p>

                <SendFields>
                    <SendInput
                        type="text"
                        placeholder="Nombre"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    {errors.name && <SendError>{errors.name}</SendError>}

                    <SendInput
                        type="email"
                        placeholder="Correo electrónico"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    {errors.email && <SendError>{errors.email}</SendError>}

                    <SendButton
                        onClick={handleSubmit}
                        disabled={!name || !email || errors.name || errors.email}
                    >
                        Enviar
                    </SendButton>
                </SendFields>
            </SendContent>
        </Modal>
    );
};
